import React, { useState } from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";

const StyledTable = styled(Table)(({ theme }) => ({
  marginTop: theme.spacing(1),
  width: "100%",
  "& thead th": {
    fontWeight: 600,
    color: theme.palette.secondary.main,
    backgroundColor: theme.palette.primary.main,
    userSelect: "text",
    pointerEvents: "auto",
    padding: theme.spacing(1),
    fontSize: "0.9rem",
    lineHeight: "1.2rem",
  },
  "& tbody td": {
    fontWeight: 300,
    padding: "2px",
    userSelect: "text",
    pointerEvents: "auto",
  },
  "& tbody tr:hover": {
    backgroundColor: "#fffbf2",
    cursor: "pointer",
  },
}));

export default function useTable(
  records,
  headCells,
  filterFn,
  helperFunctions = {}
) {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");

  const { price = () => 0, totalQuantity = () => 0 } = helperFunctions;

  const TblContainer = (props) => (
    <StyledTable>{props.children}</StyledTable>
  );

  const TblHead = () => {
    const handleSortRequest = (cellId) => {
      const isAsc = orderBy === cellId && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(cellId);
    };

    return (
      <TableHead>
        <TableRow>
          {headCells.map((headCell) => (
            <TableCell
              key={headCell.id}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              {headCell.disableSorting ? (
                headCell.label
              ) : (
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : "asc"}
                  onClick={() => handleSortRequest(headCell.id)}
                >
                  {headCell.label}
                </TableSortLabel>
              )}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  };

  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  function getComparator(order, orderBy) {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function descendingComparator(a, b, orderBy) {
    if (orderBy === "createdAt") {
      const dateA = new Date(a[orderBy]);
      const dateB = new Date(b[orderBy]);
      return dateB - dateA;
    } else if (orderBy === "staff") {
      const staffA = a.staff?.fullName.toLowerCase() || "";
      const staffB = b.staff?.fullName.toLowerCase() || "";
      return staffA < staffB ? -1 : staffA > staffB ? 1 : 0;
    } else if (orderBy === "id") {
      const idA = a.orderId ? parseInt(a.orderId, 10) : a.id;
      const idB = b.orderId ? parseInt(b.orderId, 10) : b.id;
      return idA < idB ? -1 : idA > idB ? 1 : 0;
    } else if (orderBy === "price") {
      return price(b.productLists) - price(a.productLists);
    } else if (orderBy === "quantitys") {
      return totalQuantity(b.productLists) - totalQuantity(a.productLists);
    }
    return a[orderBy] < b[orderBy] ? -1 : a[orderBy] > b[orderBy] ? 1 : 0;
  }

  const recordsAfterSortingAndFiltering = () => {
    return stableSort(filterFn.fn(records), getComparator(order, orderBy));
  };

  return {
    TblContainer,
    TblHead,
    recordsAfterSortingAndFiltering,
  };
}
